'use client'

import { useRef } from 'react'
import { useTabList } from 'react-aria'
import { TabsProps as ReactAriaTabsProps } from 'react-aria-components'
import { Item, useTabListState } from 'react-stately'

import { cn } from '../../utils/cn'
import { Stack } from '../../layouts/stack'
import { TabButton } from './tabs-button'
import { TabPanel } from './tabs-panel'

type TabsListStateOptions = Parameters<typeof useTabListState>[0]

// Enhanced TabsProps with new functionality
interface TabsProps extends ReactAriaTabsProps {
  variant?: 'pills' | 'underline' | 'bordered'
  distribution?: 'auto' | 'equal'
  className?: string
}

// Example of Tabs usage https://react-spectrum.adobe.com/react-aria/useTabList.html
/** @public */
export function Tabs({
  variant = 'pills',
  distribution = 'auto',
  className,
  ...props
}: TabsProps) {
  // For some reason children typing are incompatible
  // but it is required for correct state and rendering
  const state = useTabListState(props as TabsListStateOptions)
  const ref = useRef(null)
  const { tabListProps } = useTabList(props, state, ref)

  // Variant-specific container styles
  const containerStyles = {
    pills: 'flex w-full rounded-lg bg-white p-1',
    underline: cn(
      'flex w-full border-b border-slate-300',
      distribution === 'equal' && 'justify-center gap-16',
      distribution === 'auto' && 'justify-start gap-8'
    ),
    bordered: 'flex w-full border border-gray-200 rounded-lg p-1'
  }

  return (
    <Stack gap={4} className={cn('text-slate-950', className)}>
      <div {...tabListProps} ref={ref} className={containerStyles[variant]}>
        {[...state.collection].map(item => (
          <TabButton
            key={item.key}
            item={item}
            state={state}
            variant={variant}
            distribution={distribution}
          />
        ))}
      </div>
      <TabPanel key={state.selectedItem?.key} state={state} />
    </Stack>
  )
}

/** @public */
export const Tab = Item
